/**
 * Platform Notification Service
 * Provides event-driven notifications for the TRAZiT platform
 * Replaces console.error/warn and alerts with elegant toast notifications
 */

class NotificationService {
  constructor() {
    this.initializeEventListeners();
  }

  /**
   * Initialize event listeners for different notification types
   */
  initializeEventListeners() {
    // Listen for platform-wide error notifications
    document.addEventListener('platform-error', this.handlePlatformError.bind(this));
    
    // Listen for success notifications
    document.addEventListener('platform-success', this.handlePlatformSuccess.bind(this));
    
    // Listen for info notifications
    document.addEventListener('platform-info', this.handlePlatformInfo.bind(this));
    
    // Listen for warning notifications
    document.addEventListener('platform-warning', this.handlePlatformWarning.bind(this));
  }

  /**
   * Handle platform error notifications
   */
  handlePlatformError(event) {
    const { type, message, severity = 'error', duration = 5000 } = event.detail;
    
    // Log error for debugging
    console.error(`Platform Error [${type}]:`, message);
    
    // Show toast notification
    this.showToast({
      message: message,
      type: severity,
      duration: duration
    });
  }

  /**
   * Handle platform success notifications
   */
  handlePlatformSuccess(event) {
    const { type, message, severity = 'success', duration = 3000 } = event.detail;
    
    // Log success
    console.log(`Platform Success [${type}]:`, message);
    
    // Show toast notification
    this.showToast({
      message: message,
      type: severity,
      duration: duration
    });
  }

  /**
   * Handle platform info notifications
   */
  handlePlatformInfo(event) {
    const { type, message, severity = 'info', duration = 2000 } = event.detail;
    
    // Log info
    console.info(`Platform Info [${type}]:`, message);
    
    // Show toast notification
    this.showToast({
      message: message,
      type: severity,
      duration: duration
    });
  }

  /**
   * Handle platform warning notifications
   */
  handlePlatformWarning(event) {
    const { type, message, severity = 'warning', duration = 4000 } = event.detail;
    
    // Log warning
    console.warn(`Platform Warning [${type}]:`, message);
    
    // Show toast notification
    this.showToast({
      message: message,
      type: severity,
      duration: duration
    });
  }

  /**
   * Show toast notification: usar Common-core (globalShowNotification) cuando esté disponible;
   * si no, tr-app.setNotif o snackbar/fallback.
   */
  showToast({ message, type = 'info', duration = 3000 }) {
    const app = document.querySelector('tr-app');
    const isError = type === 'error';
    const detail = {
      message: message,
      message_en: message,
      message_es: message,
      log: true,
      is_error: isError
    };

    let lang = app?.lang;
    if (lang === undefined) {
      try {
        const userSession = JSON.parse(sessionStorage.getItem('userSession') || '{}');
        lang = userSession?.default_language || 'en';
      } catch {
        lang = 'en';
      }
    }

    // 1) Common-core: mismo camino que success/error (custom-snackbar + sessionStorage notifs)
    if (typeof window.globalShowNotification === 'function') {
      const component = app && (app.lang !== undefined) ? app : { lang };
      window.globalShowNotification(component, detail, isError);
      return;
    }

    // 2) tr-app.setNotif (toast mwc-snackbar + panel dashboard)
    if (app && typeof app.setNotif === 'function') {
      app.setNotif({ type: 'platform-notification', detail });
      return;
    }

    // 3) Respaldo: snackbar directo o fallback
    if (app?.shadowRoot) {
      const snackbar = app.shadowRoot.querySelector('mwc-snackbar');
      if (snackbar) {
        this.showSnackbarNotification(snackbar, message, type, duration);
        return;
      }
    }
    this.showFallbackToast(message, type, duration);
  }

  /**
   * Show notification using the existing mwc-snackbar
   */
  showSnackbarNotification(snackbar, message, type, duration) {
    try {
      // Set the message
      snackbar.labelText = message;
      
      // Set appearance based on type
      switch (type) {
        case 'error':
          snackbar.setAttribute('appearance', 'error');
          break;
        case 'success':
          snackbar.setAttribute('appearance', 'success');
          break;
        case 'warning':
          snackbar.setAttribute('appearance', 'warning');
          break;
        case 'info':
        default:
          snackbar.setAttribute('appearance', 'info');
          break;
      }

      // Mostrar: mwc-snackbar puede usar .show() o .open() según versión
      if (typeof snackbar.show === 'function') {
        snackbar.show();
      } else if (typeof snackbar.open === 'function') {
        snackbar.open();
      } else {
        snackbar.setAttribute('open', '');
      }

      // Auto-hide after duration
      setTimeout(() => {
        if (typeof snackbar.close === 'function') {
          snackbar.close();
        } else {
          snackbar.removeAttribute('open');
        }
      }, duration);
    } catch (error) {
      console.error('Error showing snackbar notification:', error);
      // Fallback to simple toast
      this.showFallbackToast(message, type, duration);
    }
  }

  /**
   * Fallback toast notification when snackbar is not available
   */
  showFallbackToast(message, type, duration) {
    const toast = document.createElement('div');
    toast.className = `platform-toast platform-toast-${type}`;
    toast.textContent = message;
    
    // Add to DOM
    document.body.appendChild(toast);
    
    // Show with animation
    setTimeout(() => toast.classList.add('show'), 100);
    
    // Auto-remove
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, duration);
  }

  /**
   * Dispatch error notification event
   */
  static dispatchError(type, message, severity = 'error', duration = 5000) {
    document.dispatchEvent(new CustomEvent('platform-error', {
      detail: { type, message, severity, duration },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Dispatch success notification event
   */
  static dispatchSuccess(type, message, severity = 'success', duration = 3000) {
    document.dispatchEvent(new CustomEvent('platform-success', {
      detail: { type, message, severity, duration },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Dispatch info notification event
   */
  static dispatchInfo(type, message, severity = 'info', duration = 2000) {
    document.dispatchEvent(new CustomEvent('platform-info', {
      detail: { type, message, severity, duration },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Dispatch warning notification event
   */
  static dispatchWarning(type, message, severity = 'warning', duration = 4000) {
    document.dispatchEvent(new CustomEvent('platform-warning', {
      detail: { type, message, severity, duration },
      bubbles: true,
      composed: true
    }));
  }
}

// Create and export the service instance
export const notificationService = new NotificationService();

// Export the class for instantiation
export { NotificationService };

// Export static methods for easy access
export const { dispatchError, dispatchSuccess, dispatchInfo, dispatchWarning } = NotificationService;

// Auto-initialize the service
export default notificationService;
